import { useEffect, useState } from "react";
import Cookies from "universal-cookie";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import clsx from 'clsx'

import { Reply } from "@/types"

import {
  userMe,
} from "@/store/auth";
import {
  repliesGet,
  repliesUpdate,
} from "@/store/replies";
import {
  usersUpdateUserNotAdmin,
} from "@/store/users";

import AppPageContainer from "@/components/AppPageContainer";
import AppBox from "@/components/AppBox";
import AppButton from "@/components/AppButton";
import UserConfig from "@/components/Configuration/User";
import Replies from "@/components/Configuration/Replies";

interface Config {
  company: string;
  customer_id: string;
  auto_mark_leads: boolean;
  email: string;
  emails: { id?: number; value: string }[];
  phones: { id?: number; value: string }[];
  [key: string]: any;
}

const Configuration: React.FC = () => {
  const dispatch = useAppDispatch();

  const { me } = useAppSelector((state: any) => state.auth);
  const { replies } = useAppSelector((state: any) => state.replies);

  // Impersonation detection
  const cookies = new Cookies();
  const isImpersonated = !!cookies.get("gls-agency-token");
  const adminId = isImpersonated ? me.agency?.user_id : null;
  let adminEmails: string[] = [];
  if (isImpersonated && adminId) {
    const adminUserInArray = me.agency?.users?.find((u: any) => u.id === adminId);
    
    if (adminUserInArray?.additional_data?.daily_recap_emails?.length) {
      adminEmails = adminUserInArray.additional_data.daily_recap_emails;
    } else if (me.agency?.email) {
      adminEmails = [me.agency.email];
    }
  }

  const userConfig: Config = {
    company: me.company || '',
    customer_id: me.customer_id,
    auto_mark_leads: me.auto_mark_leads,
    email: me.email,
    emails: me.emails,
    phones: me.phones,
    initial_message_emails: me.initial_message_emails,
    lead_auto_email_types: me.lead_auto_email_types,
    lead_auto_email: me.lead_auto_email,
    daily_recap_emails: me.daily_recap_emails,
    // daily_recap_emails: isImpersonated ? adminEmails : me.daily_recap_emails,
  }

  const [tempConfig, setTempConfig] = useState<Config>(userConfig);
  const [tempReplies, setTempReplies] = useState(replies);
  const [isChanged, setIsChanged] = useState(false);
  const [saveButtonClicked, setSaveButtonClicked] = useState(false);

  useEffect(() => {
    dispatch(userMe());
    dispatch(repliesGet());
  }, []);

  useEffect(() => {
    setTempConfig({
      company: me.company || '',
      customer_id: me.customer_id,
      auto_mark_leads: me.auto_mark_leads,
      email: me.email,
      emails: me.emails,
      phones: me.phones,
      initial_message_emails: me.initial_message_emails,
      lead_auto_email: me.lead_auto_email,
      lead_auto_email_types: me.lead_auto_email_types || [],
      daily_recap_emails: isImpersonated ? adminEmails : (me.daily_recap_emails || []),
    });
  }, [me, isImpersonated]);

  useEffect(() => {
    if (replies?.length) {
      setTempReplies(replies)
    }
  }, [replies]);

  const AUTO_SAVED_FIELDS = ['auto_mark_leads'];

  const handleChangeUserConfig = (field: any, value: any) => {
    setSaveButtonClicked(false)
    setTempConfig((prevConfig: any) => {
      const updatedConfig = { ...prevConfig, [field]: value };

      // Only mark as changed for fields that are NOT auto-saved
      if (!AUTO_SAVED_FIELDS.includes(field)) {
        setIsChanged(JSON.stringify(updatedConfig) !== JSON.stringify(userConfig)); // Detect changes
      }
      return updatedConfig;
    });
  };

  const isReplyContentChanged = (orig: Reply, updated: Reply) => {
    return (
      orig.text !== updated.text ||
      orig.delay !== updated.delay // we purposefully ignore .active as it is saved immediately
    );
  };

  const handleRepliesChange = (updatedReplies: Reply[]) => {
    setTempReplies(updatedReplies);

    // Determine if there are unsaved changes ignoring the `active` flag
    const hasUnsaved = updatedReplies.some((u) => {
      const o = replies.find((r: Reply) => r.id === u.id);
      return o ? isReplyContentChanged(o, u) : false;
    });
    setIsChanged(hasUnsaved);
  };

  const getChangedReplies = (originalReplies: Reply[], updatedReplies: Reply[]) => {
    return updatedReplies.filter((updatedReply) => {
      const originalReply = originalReplies.find((reply) => reply.id === updatedReply.id);
  
      // Compare only if the reply exists in the original array
      if (originalReply) {
        return isReplyContentChanged(originalReply, updatedReply);
      }
  
      return false; // In case the reply isn't found in the original array
    });
  };

  const handleSave = async () => {
    setSaveButtonClicked(true)
    // Replies Update
    const changedReplies = getChangedReplies(replies, tempReplies);

    if (changedReplies.length > 0) {
      try {
        // Loop through changed replies and dispatch API calls
        await Promise.all(
          changedReplies.map(async (reply) => {
            await dispatch(repliesUpdate({ data: reply, id: reply.id })).unwrap();
          })
        );
      } catch (error) {
        console.error("Error saving changes:", error);
      }
    }

    // User Config update
    const userInfoToUpdate = Object.keys(tempConfig).reduce((acc, key) => {
      if (tempConfig[key] !== userConfig[key]) {
        acc[key] = tempConfig[key];
      }
      return acc;
    }, {} as Record<string, any>);

    // If impersonated, move daily_recap_emails to admin updates
    let adminUpdates: Record<string, any> = {};
    if (isImpersonated && userInfoToUpdate.daily_recap_emails) {
      adminUpdates.daily_recap_emails = userInfoToUpdate.daily_recap_emails;
      delete userInfoToUpdate.daily_recap_emails;
    }    

    // If there are changes, dispatch the update
    if (Object.keys(userInfoToUpdate).length > 0) {
      await dispatch(usersUpdateUserNotAdmin({ id: me.id, data: userInfoToUpdate })).unwrap();
      await dispatch(userMe());
    }

    // Update admin if needed
    if (isImpersonated && adminId && Object.keys(adminUpdates).length > 0) {
      await dispatch(usersUpdateUserNotAdmin({ id: adminId, data: adminUpdates })).unwrap();
      await dispatch(userMe());
    }
    setIsChanged(false);
  };

  return (
    <AppPageContainer
      title="Configurations"
      titleAdorment={
        <AppBox width={210}>
          <AppButton
            variant="primary"
            disabled={!isChanged}
            className={clsx({ "animate-pulseRing": isChanged })}
            onClick={handleSave}
          >
            Save Changes
          </AppButton>
        </AppBox>
      }
    >
      <AppBox gap={4} className="flex-col lg:flex-row h-[95%]">
        {/* General Settings */}
        <AppBox flex={0.3} gap={8}>
          <UserConfig config={tempConfig} onChange={handleChangeUserConfig} isSaved={saveButtonClicked} />
        </AppBox>

        {/* Replies Settings */}
        <AppBox flex={0.7}>
          <Replies
            replies={tempReplies}
            config={tempConfig}
            onChange={handleRepliesChange}
            onConfigChange={handleChangeUserConfig}
          />
        </AppBox>
      </AppBox>
    </AppPageContainer>
  );
};

export default Configuration;